'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { SiteNav } from '@/components/ui/site-nav'
import { AmbientLayer } from '@/components/game/AmbientLayer'
import { PhaseTransition } from '@/components/game/PhaseTransition'
import { ArenaDoors } from '@/components/game/ArenaDoors'
import { MomentOverlay, type Moment } from '@/components/game/MomentOverlay'
import { PhaseCoach } from '@/components/game/PhaseCoach'
import { useNarrative } from '@/hooks/useNarrative'
import { PlayerCard, useContainmentSweep } from '@/components/game/PlayersGrid'
import { sweepDurationMs } from '@/lib/containment-sweep'
import { BarricadeBoard } from '@/components/game/BarricadeBoard'
import {
  STATIONS, assignedStation, targetStation, levelForRound,
  type BarricadeAction, type BarricadeState, type PushOutcome,
} from '@/lib/barricade'
import { OutbreakScene } from '@/components/game/OutbreakScene'
import { GameOverOverlay, type GameOutcome } from '@/components/game/GameOverOverlay'
import { checkChatMessage } from '@/lib/chatFilter'
import { PasswordInput } from '@/components/ui/password-input'
import { toast } from 'sonner'

// ── Demo limits ──────────────────────────────────────────────────────────────

const DEMO_STORAGE_KEY = 'plague_demo_count'

// The demo is uncapped. It was limited to 2 plays per device, which read as a
// conversion lever and worked as the opposite: the third visit replaced the
// game with a wall, so testers we had asked to try it simply bounced — and the
// only workaround was knowing to open a private window, which nobody does
// unprompted. It also contradicted the homepage's own "no wallet, no sign-in"
// promise. The count is still kept: it seeds the replay variation below, so
// consecutive demos are not the same game twice.

function getDemoCount(): number {
  if (typeof window === 'undefined') return 0
  return parseInt(localStorage.getItem(DEMO_STORAGE_KEY) ?? '0', 10)
}

function incrementDemoCount() {
  if (typeof window === 'undefined') return
  localStorage.setItem(DEMO_STORAGE_KEY, String(getDemoCount() + 1))
}

// ── Game constants (mirrors real-game pacing, compressed) ───────────────────

const INFECTION_SECS  = 6
const DISCUSSION_SECS = 45
const VOTING_SECS     = 30
const REVEAL_SECS     = 7
const MAX_ROUNDS      = 5
const STAKE_PER_PLAYER = 1           // USDm
const PLATFORM_FEE     = 0.015       // 1.5%, same as PlagueGame.sol

// ── Players ──────────────────────────────────────────────────────────────────

type Role = 'clean' | 'infected'

interface DemoPlayer {
  id: string
  name: string
  isYou: boolean
  status: Role                      // hidden true status
  isPatientZero: boolean
  eliminated: boolean
  shieldRound: number               // round in which Shield was activated (0 = never)
  revealedStatus: Role | null      // made public on elimination
}

const YOU_ID = 'guest'

function freshPlayers(): DemoPlayer[] {
  const base = (id: string, name: string, isYou = false): DemoPlayer => ({
    id, name, isYou, status: 'clean', isPatientZero: false, eliminated: false, shieldRound: 0, revealedStatus: null,
  })
  return [
    base(YOU_ID, 'You', true),
    base('viper', 'Viper'),
    base('atlas', 'Atlas'),
    base('echo',  'Echo'),
    base('moss',  'Moss'),
    base('rook',  'Rook'),
  ]
}

const TOTAL_PLAYERS = 6
const POT_TOTAL = TOTAL_PLAYERS * STAKE_PER_PLAYER

// ── State machine ─────────────────────────────────────────────────────────────

type DemoPhase =
  | 'welcome'
  | 'starting'
  | 'infection'
  | 'discussion'
  | 'voting'
  | 'reveal'
  | 'gameover'

interface ChatMsg {
  senderId: string
  name: string
  text: string
}

interface DemoState {
  phase: DemoPhase
  round: number
  players: DemoPlayer[]
  votes: Record<string, string>     // voterId → targetId (this round)
  eliminatedIds: string[]           // eliminated this round (for reveal panel)
  noElimination: boolean            // reveal resolved with everyone saved
  outcome: GameOutcome | null
  maxRoundsHit: boolean
  winners: string[]                 // display names
  potPerWinner: number
  shieldSet: boolean
  pzNextTarget: string | null       // Patient Zero's vote = next infection target
  infectionChain: string[]
  feed: string[]
  chat: ChatMsg[]
  /** Bots currently "composing" — drives the same indicator the live game shows. */
  typingIds: string[]
  /** Discussion-phase barricade, or null outside that window. */
  barricade: BarricadeState | null
}

const INITIAL_STATE: DemoState = {
  phase: 'welcome',
  round: 0,
  players: freshPlayers(),
  votes: {},
  eliminatedIds: [],
  noElimination: false,
  outcome: null,
  maxRoundsHit: false,
  winners: [],
  potPerWinner: 0,
  shieldSet: false,
  pzNextTarget: null,
  infectionChain: [],
  feed: [],
  chat: [],
  typingIds: [],
  barricade: null,
}

// ── Phase display (mirrors the real game page) ───────────────────────────────

// welcome/starting/gameover are demo-only staging states with no counterpart in
// a real room. The four real phases come from lib/narrative.ts so the demo and
// the live game never teach different words for the same thing.
const DEMO_ONLY_LABEL = { welcome: '', starting: 'STARTING', gameover: '' } as const
const DEMO_ONLY_COLOR = { welcome: '#6b8e23', starting: '#6b8e23', gameover: '#7d9a72' } as const

// ── Random helpers ────────────────────────────────────────────────────────────

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

function chance(p: number): boolean {
  return Math.random() < p
}

// ── Bot chatter ───────────────────────────────────────────────────────────────

const STARTING_LINES = [
  'gm survivors 🧟',
  'passwords locked. trust no one.',
  'someone in here is about to get very unlucky',
  'i have a good feeling about this village',
  'remember — no vote means you vote yourself',
]

const ACCUSE_LINES = [
  (t: string) => `${t} has been way too quiet…`,
  (t: string) => `i don't trust ${t}. no shield, no alibi.`,
  (t: string) => `watch ${t}. something is off there.`,
  (t: string) => `${t} is acting exactly like a carrier acts`,
  (t: string) => `my vote is leaning ${t} unless they shield`,
]

const DEFEND_LINES = [
  "i'm clean — i'll shield if you all come for me",
  'wasting a shield early is how clean players lose',
  'check the feed. the shields tell you who to trust.',
  'not me. look at who has NOT shielded.',
  'we vote wrong and the plague wins, just saying',
]

const SHIELDED_LINES = [
  'shield up — provably clean. focus on the rest.',
  'proof is on-chain. i am NOT the carrier.',
]

const REPLY_LINES = [
  'hmm 👀',
  'noted… but that is exactly what a carrier would say',
  'agreed.',
  'prove it — shield up.',
  'the loud ones are always sus',
  'ok but who are we actually voting?',
]

// ── Styling helpers ───────────────────────────────────────────────────────────

function playerCardStyle(p: DemoPlayer, selected: boolean): { border: string; backgroundColor: string; color: string } {
  if (p.eliminated && p.revealedStatus === 'infected') return { border: '2px solid #e63329', backgroundColor: 'rgba(230,51,41,0.12)', color: '#8a4a44' }
  if (p.eliminated) return { border: '2px solid #7d9a72', backgroundColor: 'rgba(74,94,68,0.12)', color: '#7d9a72' }
  if (selected) return { border: '2px solid #f5c518', backgroundColor: 'rgba(245,197,24,0.1)', color: '#f5c518' }
  if (p.isYou) return { border: '2px solid #6b8e23', backgroundColor: 'rgba(107,142,35,0.12)', color: '#6b8e23' }
  return { border: '2px solid rgba(107,142,35,0.3)', backgroundColor: 'rgba(107,142,35,0.05)', color: '#8fa882' }
}

function getChatBlockedReason(state: DemoState): string | null {
  const you = state.players.find(p => p.isYou)
  if (state.phase === 'gameover') return 'Chat is closed because this game has ended.'
  if (you?.eliminated) return 'Eliminated players cannot use chat.'
  if (state.phase === 'voting') return 'Chat is disabled during voting.'
  return null
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function DemoPage() {
  const narrative = useNarrative()
  const demoPhaseLabel: Record<DemoPhase, string> = {
    ...DEMO_ONLY_LABEL,
    infection:  narrative.phaseLabels.infection,
    discussion: narrative.phaseLabels.discussion,
    voting:     narrative.phaseLabels.voting,
    reveal:     narrative.phaseLabels.reveal,
  }
  const demoPhaseColor: Record<DemoPhase, string> = {
    ...DEMO_ONLY_COLOR,
    infection:  narrative.phaseColors.infection,
    discussion: narrative.phaseColors.discussion,
    voting:     narrative.phaseColors.voting,
    reveal:     narrative.phaseColors.reveal,
  }
  const [demoCount, setDemoCount] = useState(0)
  // Read inside scheduled callbacks, which would otherwise close over the value
  // that was current when the round was set up rather than the run in progress.
  const demoCountRef = useRef(0)
  useEffect(() => { demoCountRef.current = demoCount }, [demoCount])
  const [state, setState] = useState<DemoState>(INITIAL_STATE)
  const [countdown, setCountdown] = useState(0)
  const [selectedVote, setSelectedVote] = useState<string | null>(null)
  const [chatInput, setChatInput] = useState('')
  const [gameOverSeen, setGameOverSeen] = useState(false)

  const stateRef = useRef(state)
  useEffect(() => { stateRef.current = state })

  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const chatScrollRef = useRef<HTMLDivElement>(null)
  const chatPinnedRef = useRef(true)

  useEffect(() => {
    setDemoCount(getDemoCount())
  }, [])

  // Auto-scroll chat on new messages — scrolls ONLY the log container (same
  // fix as the live game page: scrollIntoView on a sentinel scrolls every
  // scrollable ancestor including the window, yanking the whole page down on
  // each bot message). Held back when the reader has scrolled up.
  useEffect(() => {
    const el = chatScrollRef.current
    if (!el || !chatPinnedRef.current) return
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [state.chat])

  // ── Timer plumbing ─────────────────────────────────────────────────────────

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout)
    timersRef.current = []
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null }
    setCountdown(0)
  }, [])

  const schedule = useCallback((ms: number, fn: () => void) => {
    timersRef.current.push(setTimeout(fn, ms))
  }, [])

  const startCountdown = useCallback((secs: number, onEnd: () => void) => {
    if (countdownRef.current) clearInterval(countdownRef.current)
    setCountdown(secs)
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null }
          return 0
        }
        return prev - 1
      })
    }, 1_000)
    schedule(secs * 1_000, onEnd)
  }, [schedule])

  useEffect(() => () => clearTimers(), [clearTimers])

  // ── Feed / chat helpers ────────────────────────────────────────────────────

  const pushFeed = useCallback((...msgs: string[]) => {
    setState(prev => ({ ...prev, feed: [...prev.feed, ...msgs].slice(-60) }))
  }, [])

  const botSay = useCallback((botId: string, text: string) => {
    setState(prev => {
      const bot = prev.players.find(p => p.id === botId)
      const typingIds = prev.typingIds.filter(id => id !== botId)
      if (!bot || bot.eliminated) return { ...prev, typingIds }
      if (prev.phase === 'voting' || prev.phase === 'gameover' || prev.phase === 'welcome') {
        return { ...prev, typingIds }
      }
      return {
        ...prev,
        typingIds,
        chat: [...prev.chat, { senderId: botId, name: bot.name, text }].slice(-200),
      }
    })
  }, [])

  /**
   * A bot composing before it speaks.
   *
   * The live game's typing indicator is a real socket signal
   * (hooks/usePresence.ts); here it is simulated, which is the honest thing for
   * a demo to do — and it matters more here than it looks. Chatter that
   * materialises instantly reads as printed output; the same line preceded by
   * two seconds of "…is typing" reads as somebody deciding what to say to you.
   *
   * The delay scales with the length of the line so a one-word reply does not
   * take as long to "write" as an accusation.
   */
  const botType = useCallback((botId: string, text: string) => {
    setState(prev => {
      const bot = prev.players.find(p => p.id === botId)
      if (!bot || bot.eliminated) return prev
      if (prev.phase === 'voting' || prev.phase === 'gameover' || prev.phase === 'welcome') return prev
      if (prev.typingIds.includes(botId)) return prev
      return { ...prev, typingIds: [...prev.typingIds, botId] }
    })
    schedule(900 + Math.min(2_600, text.length * 45), () => botSay(botId, text))
  }, [schedule, botSay])

  /** Schedule scattered bot chatter across a phase window. */
  const scheduleBotChatter = useCallback((kind: 'starting' | 'discussion', windowSecs: number) => {
    const count = kind === 'starting' ? 3 : 4 + Math.floor(Math.random() * 3)
    for (let i = 0; i < count; i++) {
      const at = 1_500 + Math.random() * (windowSecs * 1_000 * 0.8)
      schedule(at, () => {
        const s = stateRef.current
        if (kind === 'starting' && s.phase !== 'starting') return
        const aliveBots = s.players.filter(p => !p.isYou && !p.eliminated)
        if (aliveBots.length === 0) return
        const bot = pick(aliveBots)
        let line: string
        if (kind === 'starting') {
          line = pick(STARTING_LINES)
        } else if (bot.shieldRound === s.round && chance(0.5)) {
          line = pick(SHIELDED_LINES)
        } else if (chance(0.5)) {
          // Accuse a random other alive player. Infected bots never accuse teammates.
          const targets = s.players.filter(p =>
            p.id !== bot.id && !p.eliminated &&
            (bot.status !== 'infected' || p.status === 'clean')
          )
          if (targets.length === 0) return
          line = pick(ACCUSE_LINES)(pick(targets).name)
        } else {
          line = pick(DEFEND_LINES)
        }
        botType(bot.id, line)
      })
    }
  }, [schedule, botType])

  // ── Barricade (simulated) ──────────────────────────────────────────────────
  //
  // The live game resolves this on the server (backend/src/lib/barricade.ts is
  // the authority, and is where the rules are tested). The demo simulates it,
  // the same way it already simulates the chain, the votes and the payout —
  // there is no server here to ask.
  //
  // What is NOT simulated is the shape: stations and assignments come from the
  // shared lib/barricade.ts mirror, so a player who tries the demo and then
  // plays for real sees the same board laid out the same way.
  const botIntentsRef = useRef<Map<string, BarricadeAction>>(new Map())
  const [myBarricadeChoice, setMyBarricadeChoice] = useState<BarricadeAction>({ kind: 'hold' })

  /** Headcount per station. Counts only, exactly like the server's — the cam
   *  places anonymous figures from this and never learns who is who. */
  const demoOccupancy = useCallback((st: DemoState, roomKey: string): number[] => {
    const counts = [0, 0, 0, 0]
    for (const p of st.players.filter(x => !x.eliminated)) {
      const seat = st.players.indexOf(p)
      const intent = p.isYou ? myBarricadeChoice : botIntentsRef.current.get(p.id) ?? { kind: 'hold' as const }
      const at = intent.kind === 'move' ? intent.station : assignedStation(roomKey, st.round, seat)
      counts[at]++
    }
    return counts
  }, [myBarricadeChoice])

  const resolveBarricadePush = useCallback((push: number, roomKey: string) => {
    setState(prev => {
      if (prev.phase !== 'discussion' || !prev.barricade) return prev
      const station = targetStation(roomKey, prev.round, push)
      const alive = prev.players.filter(p => !p.eliminated)

      let present = 0
      let effective = 0
      const exposed: string[] = []
      for (const p of alive) {
        const seat = prev.players.indexOf(p)
        const intent = p.isYou
          ? myBarricadeChoice
          : botIntentsRef.current.get(p.id) ?? { kind: 'hold' as const }
        const at = intent.kind === 'move' ? intent.station : assignedStation(roomKey, prev.round, seat)
        if (at !== station) continue
        present++
        exposed.push(p.id)
        // Only a genuinely infected player subtracts — mirrors resolvePush.
        if (intent.kind === 'sabotage' && p.status === 'infected') effective--
        else effective++
      }

      const held = effective >= prev.barricade.threshold
      const outcome: PushOutcome = {
        push, station, held, present,
        // A station that holds names nobody; one that breaks names everyone who
        // was there, innocent or not. That asymmetry is the entire point.
        exposed: held ? [] : exposed,
        at: Date.now(),
      }
      const feed = [
        ...prev.feed,
        held
          ? `The ${STATIONS[station]} held. ${present} defended it.`
          // "BUCKLED", not "BROKE" — the boarding is battered, never breached
          // through, until the parity collapse. Matches BarricadeBoard.
          : `The ${STATIONS[station]} BUCKLED. ${present === 0 ? 'Nobody was there.' : 'Everyone there has been seen.'}`,
      ].slice(-60)

      return {
        ...prev,
        feed,
        barricade: {
          ...prev.barricade,
          next: null,
          occupancy: demoOccupancy(prev, roomKey),
          outcomes: [...prev.barricade.outcomes, outcome],
        },
      }
    })
    botIntentsRef.current.clear()
    setMyBarricadeChoice({ kind: 'hold' })
  }, [myBarricadeChoice, demoOccupancy])

  const startBarricade = useCallback((roomKey: string, windowSecs: number) => {
    botIntentsRef.current.clear()
    setMyBarricadeChoice({ kind: 'hold' })
    setState(prev => ({
      ...prev,
      barricade: {
        roomId: roomKey,
        round: prev.round,
        stations: STATIONS,
        // The night's condition. The demo runs the same arc on the same rounds
        // as the live game so the two teach the same thing.
        threshold: levelForRound(prev.round).threshold,
        pushes: levelForRound(prev.round).pushes,
        level: levelForRound(prev.round).label,
        occupancy: demoOccupancy(prev, roomKey),
        next: null,
        outcomes: [],
      },
    }))

    // Evenly spread between the same bounds the server uses, for however many
    // pushes this round's level calls for.
    const count = levelForRound(stateRef.current.round).pushes
    const fractions = Array.from({ length: count }, (_, i) =>
      count <= 1 ? 0.45 : 0.25 + (0.82 - 0.25) * (i / (count - 1)))
    fractions.forEach((f, push) => {
      const at = windowSecs * 1_000 * f
      schedule(Math.max(0, at - 8_000), () => {
        const now = stateRef.current
        if (now.phase !== 'discussion') return
        const station = targetStation(roomKey, now.round, push)

        // Bot intents, decided at the warning. Most hold — that is the common,
        // unremarkable play, and it is what makes silence uninformative. A few
        // reposition; infected bots sometimes sabotage where they stand.
        for (const bot of now.players.filter(p => !p.isYou && !p.eliminated)) {
          if (bot.status === 'infected' && chance(0.45)) {
            botIntentsRef.current.set(bot.id, { kind: 'sabotage' })
          } else if (chance(0.3)) {
            botIntentsRef.current.set(bot.id, { kind: 'move', station })
          }
        }

        setState(p => p.barricade
          ? {
              ...p,
              barricade: {
                ...p.barricade,
                occupancy: demoOccupancy(p, roomKey),
                next: { push, station, at: Date.now() + 8_000 },
              },
            }
          : p)
      })
      schedule(at, () => {
        if (stateRef.current.phase !== 'discussion') return
        resolveBarricadePush(push, roomKey)
      })
    })
  }, [schedule, resolveBarricadePush, demoOccupancy])

  // ── Phase engine ───────────────────────────────────────────────────────────
  // Forward declarations via refs so callbacks can chain in any order.
  const startRoundRef = useRef<(n: number) => void>(() => {})
  const goVotingRef = useRef<() => void>(() => {})
  const resolveVotesRef = useRef<() => void>(() => {})
  const checkEndRef = useRef<() => void>(() => {})

  const finishGame = useCallback((outcome: GameOutcome, maxRoundsHit = false) => {
    clearTimers()
    setState(prev => {
      const alive = prev.players.filter(p => !p.eliminated)
      // Payout must mirror PlagueGame._distributePot: a faction win pays the
      // surviving members of that faction, a draw pays EVERY survivor
      // regardless of status. The demo teaches the rules for a real-money
      // game — it can't diverge from the contract.
      let winners: DemoPlayer[] = []
      if (outcome === 'clean_win') winners = alive.filter(p => p.status === 'clean')
      else if (outcome === 'infected_win') winners = alive.filter(p => p.status === 'infected')
      else winners = alive
      const potNet = POT_TOTAL * (1 - PLATFORM_FEE)
      const per = winners.length > 0 ? potNet / winners.length : 0
      const feed = [...prev.feed]
      if (maxRoundsHit) feed.push(`Round limit reached (${MAX_ROUNDS}) — the game is a draw; every survivor splits the pot.`)
      feed.push(`Game over: ${outcome.replaceAll('_', ' ')}. Pot ${POT_TOTAL} USDm − ${PLATFORM_FEE * 100}% fee → ${per > 0 ? `${per.toFixed(2)} USDm per winner.` : 'no faction payout.'}`)
      // Reveal everyone at game end, like the real post-game state.
      const players = prev.players.map(p => ({ ...p, revealedStatus: p.status }))
      return {
        ...prev,
        phase: 'gameover',
        typingIds: [],
        players,
        outcome,
        maxRoundsHit,
        winners: winners.map(w => w.name),
        potPerWinner: per,
        feed: feed.slice(-60),
      }
    })
  }, [clearTimers])

  const checkEnd = useCallback(() => {
    const s = stateRef.current
    const alive = s.players.filter(p => !p.eliminated)
    const infected = alive.filter(p => p.status === 'infected').length
    const clean = alive.length - infected
    if (infected === 0) { finishGame('clean_win'); return }
    if (infected === 1 && clean === 1) { finishGame('draw'); return }
    if (infected > clean) { finishGame('infected_win'); return }
    if (s.round >= MAX_ROUNDS) { finishGame('draw', true); return }
    startRoundRef.current(s.round + 1)
  }, [finishGame])

  const resolveVotes = useCallback(() => {
    const s0 = stateRef.current
    if (s0.phase !== 'voting') return
    clearTimers()
    setState(prev => {
      const alive = prev.players.filter(p => !p.eliminated)
      const votes = { ...prev.votes }
      const feed = [...prev.feed]

      // Absent-vote rule: silence equals a self-vote — same as the contract.
      for (const p of alive) {
        if (!votes[p.id]) {
          votes[p.id] = p.id
          feed.push(`${p.name} did not vote — self-vote recorded.`)
        }
      }

      const tally: Record<string, number> = {}
      for (const target of Object.values(votes)) tally[target] = (tally[target] ?? 0) + 1
      const max = Math.max(...Object.values(tally))
      const tiedIds = Object.keys(tally).filter(id => tally[id] === max)
      const tied = tiedIds.map(id => prev.players.find(p => p.id === id)!).filter(p => !p.eliminated)

      const vulnerable = tied.filter(p => p.shieldRound !== prev.round)
      let eliminatedIds: string[] = []
      if (vulnerable.length === 0) {
        feed.push('Top votes were tied and every top-voted player had an active Shield — nobody was eliminated.')
      } else {
        // Mirror the contract: exactly ONE unprotected top-voted player falls
        // (deterministic lowest-hash on-chain; random here) — and no peeking
        // at hidden roles. The old logic preferred eliminating the infected
        // from a tie, which rigged most demo games to end in round 1.
        eliminatedIds = [pick(vulnerable).id]
        const saved = tied.filter(p => p.shieldRound === prev.round)
        for (const p of saved) feed.push(`${p.name} was top-voted but their Shield saved them.`)
      }

      let players = prev.players.map(p =>
        eliminatedIds.includes(p.id)
          ? { ...p, eliminated: true, revealedStatus: p.status }
          : p
      )
      for (const id of eliminatedIds) {
        const p = players.find(pl => pl.id === id)!
        const status = p.status === 'infected' ? 'INFECTED ☣' : 'CLEAN ✚'
        feed.push(p.isYou
          ? `You have been eliminated — you were ${status}.`
          : `${p.name} has been eliminated — they were ${status}.`)
      }

      // Patient Zero succession: promote the next alive infected in the chain.
      const pzDown = players.find(p => p.isPatientZero && p.eliminated)
      if (pzDown) {
        players = players.map(p => (p.id === pzDown.id ? { ...p, isPatientZero: false } : p))
        const nextId = prev.infectionChain.find(id => {
          const p = players.find(pl => pl.id === id)
          return p && !p.eliminated && p.status === 'infected'
        })
        if (nextId) {
          players = players.map(p => (p.id === nextId ? { ...p, isPatientZero: true } : p))
          feed.push('Patient Zero has fallen — but the infection chain promotes a new carrier…')
          if (nextId === YOU_ID) feed.push('☣ You are now Patient Zero. Your vote secretly marks the next infection target.')
        }
      }

      return { ...prev, phase: 'reveal', typingIds: [], players, votes, eliminatedIds, noElimination: eliminatedIds.length === 0, feed: feed.slice(-60) }
    })
    startCountdown(REVEAL_SECS, () => checkEndRef.current())
  }, [clearTimers, startCountdown])

  /** Register a vote, announce it, and short-circuit the phase once everyone voted. */
  const addVote = useCallback((voterId: string, targetId: string) => {
    setState(prev => {
      if (prev.phase !== 'voting' || prev.votes[voterId]) return prev
      const voter = prev.players.find(p => p.id === voterId)
      if (!voter || voter.eliminated) return prev
      const feed = [...prev.feed, voter.isYou ? 'You have voted.' : `${voter.name} has voted.`]
      return {
        ...prev,
        votes: { ...prev.votes, [voterId]: targetId },
        // Patient Zero's vote doubles as the next infection target — real mechanic.
        pzNextTarget: voter.isPatientZero ? targetId : prev.pzNextTarget,
        feed: feed.slice(-60),
      }
    })
    schedule(80, () => {
      const s = stateRef.current
      if (s.phase !== 'voting') return
      const alive = s.players.filter(p => !p.eliminated)
      if (alive.every(p => s.votes[p.id])) {
        clearTimers()
        pushFeed('All votes are in — tallying early.')
        schedule(1_500, () => resolveVotesRef.current())
      }
    })
  }, [schedule, clearTimers, pushFeed])

  const goVoting = useCallback(() => {
    const s0 = stateRef.current
    if (s0.phase !== 'discussion') return
    clearTimers()
    setSelectedVote(null)
    setState(prev => ({
      ...prev,
      phase: 'voting',
      // clearTimers() above cancels any pending botSay, so the composing set has
      // to be dropped with it or a bot types forever into a silent phase.
      typingIds: [],
      // 🚨 The barricade state SURVIVES into voting. Clearing it here dropped
      // the round's outcomes, so any wall that had been breached boarded itself
      // back up the instant Discussion ended. The mechanic is gated on the
      // phase, not on the state existing; the state is cleared at the next
      // round instead, which is the survivors patching it between nights.
      // (Matches app/game/page.tsx, where the server's state persists likewise.)
      feed: [...prev.feed, 'Voting is open. Skipping your vote records a self-vote against you.'].slice(-60),
    }))
    // Bots vote at scattered times.
    const s = stateRef.current
    for (const bot of s.players.filter(p => !p.isYou && !p.eliminated)) {
      schedule(2_000 + Math.random() * VOTING_SECS * 1_000 * 0.7, () => {
        const now = stateRef.current
        if (now.phase !== 'voting') return
        const me = now.players.find(p => p.id === bot.id)
        if (!me || me.eliminated || now.votes[me.id]) return
        const others = now.players.filter(p => p.id !== me.id && !p.eliminated)
        let pool: DemoPlayer[]
        if (me.status === 'infected') {
          // Infected target clean players, preferring unshielded ones.
          const cleanTargets = others.filter(p => p.status === 'clean')
          const unshielded = cleanTargets.filter(p => p.shieldRound !== now.round)
          pool = unshielded.length > 0 ? unshielded : (cleanTargets.length > 0 ? cleanTargets : others)
        } else {
          // Clean bots trust anyone whose Shield proved them clean this round.
          const suspects = others.filter(p => p.shieldRound !== now.round)
          pool = suspects.length > 0 ? suspects : others
        }
        addVote(me.id, pick(pool).id)
      })
    }
    startCountdown(VOTING_SECS, () => resolveVotesRef.current())
  }, [clearTimers, schedule, startCountdown, addVote])

  const startRound = useCallback((n: number) => {
    clearTimers()
    setSelectedVote(null)
    setState(prev => ({
      ...prev,
      phase: 'infection',
      round: n,
      typingIds: [],
      barricade: null,
      votes: {},
      eliminatedIds: [],
      noElimination: false,
      feed: [...prev.feed, `Round ${n} — infection phase. ${n === 1 ? 'Patient Zero is about to emerge…' : 'The plague is choosing its next host…'}`].slice(-60),
    }))
    startCountdown(INFECTION_SECS, () => {
      // Target is picked from the CURRENT state rather than inside the updater
      // so the post-infection parity check below can run before we commit to
      // opening discussion. stateRef is fresh here — this fires INFECTION_SECS
      // after the phase change that scheduled it.
      const s0 = stateRef.current
      const aliveClean = s0.players.filter(p => !p.eliminated && p.status === 'clean')
      let targetId: string | null = null
      if (n === 1) {
        // First infection is random — that player IS Patient Zero.
        targetId = aliveClean.length > 0 ? pick(aliveClean).id : null
      } else {
        const wanted = s0.pzNextTarget
        const valid = wanted && aliveClean.some(p => p.id === wanted)
        targetId = valid ? wanted : (aliveClean.length > 0 ? pick(aliveClean).id : null)
      }

      // Endgame parity, evaluated AFTER the infection lands — mirrors
      // PlagueGame.assignInfection (L613-622). Without this the demo only
      // checked parity after elimination, so an infection that pushed the
      // infected into the majority went unnoticed until the NEXT reveal: the
      // game visibly played on a full round past the point it was decided.
      // As in the contract, the very first infection merely starts the
      // outbreak and can never end the game.
      const aliveAfter = s0.players.filter(p => !p.eliminated)
      const infectedAfter = aliveAfter.filter(p => p.status === 'infected' || p.id === targetId).length
      const decided = n > 1 && infectedAfter > aliveAfter.length - infectedAfter

      setState(prev => {
        const feed = [...prev.feed]
        let players = prev.players
        let chain = prev.infectionChain

        if (targetId) {
          players = players.map(p =>
            p.id === targetId ? { ...p, status: 'infected' as Role, isPatientZero: n === 1 } : p
          )
          chain = [...chain, targetId]
          feed.push(n === 1
            ? 'Patient Zero has emerged. Someone in this room is now infected — and only they know it.'
            : 'The infection has spread to a new host. Only they were notified.')
          if (targetId === YOU_ID) {
            feed.push(n === 1
              ? '☣ You are Patient Zero. Blend in — your vote secretly marks the next infection target.'
              : '⚠ You have been infected. You can no longer activate a Shield — blend in.')
          }
        } else {
          feed.push('The plague found no new host this round.')
        }

        if (decided) {
          feed.push('The infected now outnumber the living. There is no vote left to win.')
          return { ...prev, players, infectionChain: chain, pzNextTarget: null, feed: feed.slice(-60) }
        }

        feed.push('Discussion is open. Clean players can activate a Shield to prove innocence.')
        return { ...prev, phase: 'discussion', players, infectionChain: chain, pzNextTarget: null, feed: feed.slice(-60) }
      })

      // Decided by the infection itself — end here instead of playing out a
      // discussion and vote whose outcome cannot matter.
      if (decided) {
        finishGame('infected_win')
        return
      }

      startCountdown(DISCUSSION_SECS, () => goVotingRef.current())
      scheduleBotChatter('discussion', DISCUSSION_SECS)
      startBarricade(`demo-${demoCountRef.current}`, DISCUSSION_SECS)

      // Some clean bots prove innocence with a Shield mid-discussion.
      const s = stateRef.current
      for (const bot of s.players.filter(p => !p.isYou && !p.eliminated)) {
        if (!chance(0.4)) continue
        schedule(3_000 + Math.random() * DISCUSSION_SECS * 1_000 * 0.55, () => {
          const now = stateRef.current
          if (now.phase !== 'discussion') return
          const me = now.players.find(p => p.id === bot.id)
          // Infected players cannot produce a valid proof — the circuit rejects them.
          if (!me || me.eliminated || me.status === 'infected' || me.shieldRound === now.round) return
          setState(prev => ({
            ...prev,
            players: prev.players.map(p => p.id === me.id ? { ...p, shieldRound: prev.round } : p),
            feed: [...prev.feed, `${me.name} activated a Shield — verified clean this round.`].slice(-60),
          }))
        })
      }
    })
  }, [clearTimers, startCountdown, schedule, scheduleBotChatter, finishGame, startBarricade])

  // Keep the forward-declaration refs pointing at the latest callbacks.
  useEffect(() => {
    startRoundRef.current = startRound
    goVotingRef.current = goVoting
    resolveVotesRef.current = resolveVotes
    checkEndRef.current = checkEnd
  }, [startRound, goVoting, resolveVotes, checkEnd])

  // ── User actions ───────────────────────────────────────────────────────────

  const startDemo = useCallback(() => {
    incrementDemoCount()
    setDemoCount(getDemoCount())
    setGameOverSeen(false)
    clearTimers()
    setState({
      ...INITIAL_STATE,
      players: freshPlayers(),
      phase: 'starting',
      feed: [`Room "The Cursed Village" filled — ${TOTAL_PLAYERS} players staked ${STAKE_PER_PLAYER} USDm each. Pot: ${POT_TOTAL} USDm.`],
    })
    scheduleBotChatter('starting', 12)
  }, [clearTimers, scheduleBotChatter])

  const resetDemo = useCallback(() => {
    clearTimers()
    setGameOverSeen(false)
    setSelectedVote(null)
    setChatInput('')
    setState(INITIAL_STATE)
  }, [clearTimers])

  const commitShield = useCallback((password: string) => {
    if (!password.trim()) return
    setState(prev => ({
      ...prev,
      shieldSet: true,
      feed: [...prev.feed, 'Shield Password set (ZK commitment registered). Waiting for the other players…'].slice(-60),
    }))
    schedule(1_800, () => pushFeed('All players locked in. The game is starting.'))
    schedule(3_000, () => startRoundRef.current(1))
  }, [schedule, pushFeed])

  const activateShield = useCallback(() => {
    setState(prev => {
      const you = prev.players.find(p => p.isYou)!
      if (prev.phase !== 'discussion' || you.eliminated || you.status === 'infected' || you.shieldRound === prev.round) return prev
      return {
        ...prev,
        players: prev.players.map(p => p.isYou ? { ...p, shieldRound: prev.round } : p),
        feed: [...prev.feed, 'Your Shield is active — you are provably clean this round. A top vote cannot eliminate you.'].slice(-60),
      }
    })
  }, [])

  const castVote = useCallback(() => {
    if (!selectedVote) return
    addVote(YOU_ID, selectedVote)
    setSelectedVote(null)
  }, [selectedVote, addVote])

  const sendChat = useCallback(() => {
    if (!chatInput.trim()) return
    const s = stateRef.current
    if (getChatBlockedReason(s)) return
    // Same screen as the real room. The demo never touches the backend, so this
    // copy is the only thing standing between the box and the transcript, and
    // it is the page a curious reviewer reaches first since it needs no wallet.
    const screened = checkChatMessage(chatInput)
    if (!screened.ok) {
      toast.error(screened.reason)
      return
    }
    const text = screened.text
    setState(prev => ({ ...prev, chat: [...prev.chat, { senderId: YOU_ID, name: 'You', text }].slice(-200) }))
    setChatInput('')
    // Bots often react to what you say.
    if (chance(0.65)) {
      schedule(1_200 + Math.random() * 2_500, () => {
        const now = stateRef.current
        const bots = now.players.filter(p => !p.isYou && !p.eliminated)
        if (bots.length > 0) botType(pick(bots).id, pick(REPLY_LINES))
      })
    }
  }, [chatInput, schedule, botType])

  // ── Derived ────────────────────────────────────────────────────────────────

  const { phase, round, players, votes, eliminatedIds, noElimination, outcome, maxRoundsHit, winners, potPerWinner, shieldSet, feed, chat, typingIds, barricade } = state

  // Round-opening containment sweep — the same beat, and the same timing rules,
  // the live game runs (lib/containment-sweep.ts). Seeded from the demo run and
  // round so it differs each round; anchored to the moment the infection phase
  // opened. Status-blind here too: it takes a seat count, not the roster.
  const [demoSweep, setDemoSweep] = useState<{ seed: string; anchor: number } | null>(null)
  useEffect(() => {
    if (phase !== 'infection' || round < 1) { setDemoSweep(null); return }
    setDemoSweep({ seed: `demo-${demoCount}:${round}`, anchor: Date.now() })
    const t = setTimeout(() => setDemoSweep(null), sweepDurationMs(players.length))
    return () => clearTimeout(t)
  // Keyed to the round opening, not the roster: re-running as players change
  // would restart the beat mid-sweep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, round, demoCount])
  const demoScanStates = useContainmentSweep(players.length, demoSweep?.seed ?? null, demoSweep?.anchor ?? 0)

  const you = players.find(p => p.isYou)!

  // ── Personal moment overlays (same beats as the live game) ────────────────
  const [moment, setMoment] = useState<{ key: string; data: Moment } | null>(null)
  // Infection reveal — fires on your clean→infected flip, any code path.
  const prevYouStatusRef = useRef(you.status)
  useEffect(() => {
    const prev = prevYouStatusRef.current
    prevYouStatusRef.current = you.status
    if (prev !== 'infected' && you.status === 'infected') {
      setMoment({
        key: `infected:${Date.now()}`,
        data: {
          label: 'You Are Infected',
          color: '#e63329',
          glyph: '☣',
          sublabel: 'Hide it. Spread it. Survive the votes.',
          intense: true,
        },
      })
    }
  }, [you.status])
  // Shield activation — fires when your shieldRound lands on a new round.
  const prevShieldRoundRef = useRef(you.shieldRound)
  useEffect(() => {
    const prev = prevShieldRoundRef.current
    prevShieldRoundRef.current = you.shieldRound
    if (you.shieldRound > 0 && you.shieldRound !== prev) {
      setMoment({
        key: `shield:${you.shieldRound}`,
        data: {
          label: 'Shield Active',
          color: '#6b8e23',
          glyph: '✚',
          sublabel: `Round ${you.shieldRound} — provably clean`,
        },
      })
    }
  }, [you.shieldRound])
  const alivePlayers = players.filter(p => !p.eliminated)
  const infectedAlive = alivePlayers.filter(p => p.status === 'infected').length
  // Quarantine-cam adapter: the scene wants the same public counts + own-status
  // shape the real game page feeds it.
  let youSceneStatus: 'clean' | 'infected' | 'eliminated' = you.status
  if (you.eliminated) youSceneStatus = 'eliminated'
  let sceneOutcome: 'clean_win' | 'infected_win' | 'max_rounds_draw' | null = null
  if (outcome === 'clean_win' || outcome === 'infected_win') sceneOutcome = outcome
  else if (outcome) sceneOutcome = 'max_rounds_draw'

  // 🚨 MEMOIZED. The scene reads a new object here as "the board changed" and
  // re-aims every figure; a fresh literal on every render did that dozens of
  // times a second, so the room twitched in place instead of walking anywhere.
  // Same reasoning, same shape, as the live game page.
  const youSeat = players.findIndex(p => p.isYou)
  const youEliminated = you.eliminated
  const barricadeChoiceStation = myBarricadeChoice.kind === 'move' ? myBarricadeChoice.station : null
  const demoBarricadeView = useMemo(() => {
    // The compound stands for the whole game INCLUDING the finish. It used to
    // vanish at game over, which dropped the scene back to figures in an empty
    // void for the one moment the player is looking hardest.
    const running = phase === 'discussion' && !!barricade
    const myStation = !running || !barricade || youEliminated || youSeat < 0
      ? null
      // The wall they actually chose, not the one they were assigned —
      // otherwise tapping a wall moves nothing.
      : barricadeChoiceStation ?? assignedStation(barricade.roomId, barricade.round, youSeat)
    return {
      active: running,
      occupancy: running ? barricade?.occupancy ?? [] : [],
      threatened: running ? barricade?.next?.station ?? null : null,
      myStation,
      resultKey: barricade?.outcomes.length ?? 0,
      held: barricade?.outcomes[barricade.outcomes.length - 1]?.held ?? null,
      // Damage outlives the phase — see the note in app/game/page.tsx. Gating
      // this on the barricade still running had breached walls boarding
      // themselves back up the instant Discussion ended.
      brokenWalls: barricade?.outcomes.filter(o => !o.held).map(o => o.station) ?? [],
      infectedCount: infectedAlive,
      collapsed: infectedAlive > alivePlayers.length - infectedAlive,
    }
  }, [phase, barricade, youSeat, youEliminated, barricadeChoiceStation, infectedAlive, alivePlayers.length])
  const youVoted = Boolean(votes[YOU_ID])
  const canVote = phase === 'voting' && !you.eliminated && !youVoted
  const youShielded = you.shieldRound === round && round > 0
  const chatBlockedReason = getChatBlockedReason(state)

  const voteTally: Record<string, number> = {}
  for (const target of Object.values(votes)) voteTally[target] = (voteTally[target] ?? 0) + 1

  // Feed + role cards render in the left column on desktop (mirrors the real
  // game page) but stay below the chat in the single-column mobile stack.
  const feedCard = (
    <div className="rounded-lg border p-5" style={{ backgroundColor: '#0a100a', borderColor: 'rgba(107,142,35,0.15)' }}>
      <p className="font-mono text-xs uppercase tracking-[0.2em] mb-3" style={{ color: '#6b8e23' }}>Live Feed</p>
      <ul className="space-y-2 font-mono text-xs overflow-y-auto" style={{ color: '#8fa882', maxHeight: '16rem', scrollbarWidth: 'thin' }}>
        {feed.length === 0 ? (
          <li style={{ color: '#7d9a72' }}>No events yet.</li>
        ) : (
          [...feed].reverse().map((msg, i) => (
            <li key={`${msg}-${i}`} className="flex gap-2">
              <span style={{ color: '#7d9a72' }}>→</span> {msg}
            </li>
          ))
        )}
      </ul>
    </div>
  )
  const roleCard = <YourRoleCard you={you} />

  // ── Welcome / limit screens ────────────────────────────────────────────────

  if (phase === 'welcome') {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-6 gap-8" style={{ backgroundColor: '#060b06', color: '#d4c9b2', backgroundImage: 'url(/images/bg-lobby.webp)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="fixed inset-0" style={{ backgroundColor: 'rgba(6,11,6,0.88)' }} />
        <div className="relative z-10 flex flex-col items-center gap-8 text-center max-w-lg">
          <div className="flex flex-col items-center gap-3">
            <span className="font-mono text-xs uppercase tracking-[0.3em]" style={{ color: '#6b8e23' }}>Free Demo</span>
            <h1
              className="font-display text-5xl sm:text-7xl leading-none font-bold"
              style={{ background: 'linear-gradient(135deg, #cc1414, #c8b89a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
            >
              TRY THE PLAGUE
            </h1>
            <p className="font-mono text-sm leading-relaxed max-w-sm" style={{ color: '#8fa882' }}>
              A full simulated match — {TOTAL_PLAYERS} players, live chat, a random Patient Zero, real voting rules. Exactly like the live game, without staking a single USDm.
            </p>
          </div>

          <div className="w-full rounded-lg border p-5 space-y-3" style={{ backgroundColor: '#0a100a', borderColor: 'rgba(107,142,35,0.25)' }}>
            <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#7d9a72' }}>What you&apos;ll experience</p>
            {[
              'A random player becomes Patient Zero — it could be you',
              'Chat with the room: accuse, defend, bluff',
              'The infection spreads round after round',
              'Activate your Shield (ZK proof) to prove innocence',
              'Vote each round — skip it and you vote yourself',
              'Survive until one side takes the pot',
            ].map(step => (
              <div key={step} className="flex items-start gap-2 text-left">
                <span style={{ color: '#6b8e23' }}>→</span>
                <span className="font-mono text-xs" style={{ color: '#8fa882' }}>{step}</span>
              </div>
            ))}
          </div>

          <button
            onClick={startDemo}
            className="w-full rounded-lg py-4 font-mono text-sm font-bold uppercase tracking-widest transition-all hover:opacity-90 active:scale-95"
            style={{ backgroundColor: '#cc1414', color: '#ffffff', boxShadow: '4px 4px 0 #6b8e23' }}
          >
            Start Demo →
          </button>

          <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: '#7d9a72' }}>
            Free to play, as many times as you like · no sign-in needed
          </p>

          <Link href="/lobby" className="font-mono text-xs underline transition-opacity hover:opacity-70" style={{ color: '#6b8e23' }}>
            Skip demo — sign in and play →
          </Link>
        </div>
      </main>
    )
  }

  // ── Active game UI ─────────────────────────────────────────────────────────

  return (
    <main className="min-h-screen" style={{ backgroundColor: '#060b06', color: '#d4c9b2', backgroundImage: 'url(/images/bg-game.webp)', backgroundSize: 'cover', backgroundPosition: 'center top', backgroundAttachment: 'fixed' }}>
      <div className="fixed inset-0 pointer-events-none" style={{ backgroundColor: 'rgba(6,11,6,0.88)', zIndex: 0 }} />
      {/* Keyed per demo run so the entrance replays on every Start Demo. */}
      <ArenaDoors roomId={`demo-${demoCount}`} />
      <AmbientLayer />
      <PhaseTransition
        phaseKey={`${round}:${phase}`}
        label={demoPhaseLabel[phase]}
        color={demoPhaseColor[phase]}
        sublabel={round > 0 ? `Round ${round}` : undefined}
        glyphKey={phase}
        enabled={phase !== 'gameover' && demoPhaseLabel[phase] !== ''}
      />
      <MomentOverlay momentKey={moment?.key ?? null} moment={moment?.data ?? null} />
      {/* Just-in-time guidance. Sits after the overlays so it layers above the
          table, but it is non-blocking and never eats a timed phase. */}
      <PhaseCoach phase={phase} />
      {phase === 'gameover' && outcome && !gameOverSeen && (
        <GameOverOverlay
          outcome={outcome}
          potPerWinner={potPerWinner}
          winners={winners}
          onDismiss={() => setGameOverSeen(true)}
        />
      )}
      <div className="relative" style={{ zIndex: 1 }}>

        {/* Nav */}
        <div className="sticky top-0 z-50 px-4 pt-4 sm:px-8 sm:pt-6">
          <div className="mx-auto w-full max-w-6xl">
            <SiteNav currentPath="/demo" />
          </div>
        </div>

        {/* Demo banner */}
        <div className="px-6 pt-4">
          <div className="mx-auto w-full max-w-6xl">
            <div
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-2"
              style={{ backgroundColor: 'rgba(245,197,24,0.06)', borderColor: 'rgba(245,197,24,0.3)' }}
            >
              <span className="font-mono text-xs" style={{ color: '#f5c518' }}>
                ⚠ Demo mode · simulated players · no real tokens
              </span>
              <Link
                href="/lobby"
                className="flex-shrink-0 whitespace-nowrap font-mono text-[10px] uppercase tracking-wider rounded border px-2 py-1 transition-all hover:opacity-80"
                style={{ borderColor: 'rgba(107,142,35,0.4)', color: '#6b8e23' }}
              >
                Play for Real →
              </Link>
            </div>
          </div>
        </div>

        {/* Header */}
        <header className="px-6 py-8" style={{ borderBottom: '1px solid rgba(107,142,35,0.2)' }}>
          <div className="mx-auto w-full max-w-6xl">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: '#7d9a72' }}>Demo Room</p>
                <p className="font-heading text-2xl leading-none" style={{ color: '#e63329' }}>The Cursed Village</p>
              </div>
              {phase !== 'gameover' && (
                <span
                  className="rounded border px-3 py-1 font-mono text-xs uppercase tracking-[0.2em]"
                  style={{
                    borderColor: `${demoPhaseColor[phase]}44`,
                    backgroundColor: `${demoPhaseColor[phase]}18`,
                    color: demoPhaseColor[phase],
                  }}
                >
                  {demoPhaseLabel[phase]}
                </span>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-4">
              <div className="text-center">
                <p className="font-mono text-[10px] uppercase" style={{ color: '#7d9a72' }}>Round</p>
                <p className="font-heading text-3xl leading-none" style={{ color: '#d4c9b2' }}>{round}</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-[10px] uppercase" style={{ color: '#7d9a72' }}>Alive</p>
                <p className="font-heading text-3xl leading-none" style={{ color: '#d4c9b2' }}>{alivePlayers.length}/{TOTAL_PLAYERS}</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-[10px] uppercase" style={{ color: '#7d9a72' }}>Pot</p>
                <p className="font-heading text-3xl leading-none" style={{ color: '#f5c518' }}>{POT_TOTAL} USDm</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-[10px] uppercase" style={{ color: '#7d9a72' }}>Infected</p>
                <p className="font-heading text-3xl leading-none" style={{ color: '#e63329' }}>
                  {phase === 'gameover' ? infectedAlive : '?'}
                </p>
              </div>
              {countdown > 0 && (
                <div className="text-center">
                  <p className="font-mono text-[10px] uppercase" style={{ color: '#7d9a72' }}>Time Left</p>
                  <p className="font-mono text-3xl tabular-nums leading-none" style={{ color: '#6b8e23' }}>{String(Math.floor(countdown / 60)).padStart(2, '0')}:{String(countdown % 60).padStart(2, '0')}</p>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main */}
        <div className="px-6 py-8">
          <div className="mx-auto w-full max-w-6xl">
            <div className="grid gap-6 lg:grid-cols-[1fr_320px]">

              {/* Left column */}
              <div className="flex flex-col gap-6">

                {/* Set Shield Password — hoisted ABOVE the board. This is a
                    required action, and during `starting` the board below has
                    nothing to show yet (nobody infected, nobody voted, the cam
                    is idle), so leading with the action costs no context. Every
                    later phase keeps its panel under the grid, where the grid
                    IS the interface (tap a player, then act). */}
                {phase === 'starting' && (
                  <StartingPanel shieldSet={shieldSet} onCommit={commitShield} />
                )}

                {/* Player grid */}
                <article className="rounded-lg border p-5" style={{ backgroundColor: '#0a100a', borderColor: 'rgba(107,142,35,0.2)' }}>
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <h2 className="font-heading text-xl leading-none" style={{ color: '#d4c9b2' }}>Area 51</h2>
                    <span className="font-mono text-xs rounded border px-2 py-0.5" style={{ borderColor: 'rgba(107,142,35,0.3)', color: '#6b8e23' }}>{alivePlayers.length} alive</span>
                  </div>
                  <OutbreakScene
                    barricade={demoBarricadeView}
                    className="mb-4"
                    totalPlayers={players.length}
                    aliveCount={alivePlayers.length}
                    zombieCount={infectedAlive}
                    myStatus={youSceneStatus}
                    outcome={sceneOutcome}
                    myShieldActive={youShielded}
                    othersShieldCount={round > 0 ? players.filter(p => !p.isYou && !p.eliminated && p.shieldRound === round).length : 0}
                  />
                  {phase === 'discussion' && barricade && (() => {
                    const you = players.find(p => p.isYou)
                    const mySeat = you ? players.indexOf(you) : -1
                    return (
                      <div className="mb-4">
                        <BarricadeBoard
                          state={barricade}
                          myStation={you && !you.eliminated && mySeat >= 0
                            ? assignedStation(barricade.roomId, barricade.round, mySeat)
                            : null}
                          myChoice={myBarricadeChoice}
                          canSabotage={!!you && !you.eliminated && you.status === 'infected'}
                          disabled={!you || you.eliminated}
                          nameOf={id => players.find(p => p.id === id)?.name ?? id}
                          onChoose={setMyBarricadeChoice}
                        />
                      </div>
                    )
                  })()}

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {players.map((p, i) => {
                      const selected = phase === 'voting' && selectedVote === p.id
                      const justRevealed = phase === 'reveal' && eliminatedIds.includes(p.id)
                      const clickable = canVote && !p.isYou && !p.eliminated
                      const tallyCount = voteTally[p.id] ?? 0
                      return (
                        <PlayerCard
                          key={p.id}
                          index={i}
                          name={p.name}
                          style={playerCardStyle(p, selected)}
                          isMe={p.isYou}
                          selected={selected}
                          eliminated={p.eliminated}
                          justEliminated={justRevealed}
                          votedByMe={phase === 'voting' && votes[YOU_ID] === p.id}
                          clickable={clickable}
                          scanState={demoScanStates[i] ?? 'idle'}
                          onClick={() => clickable && setSelectedVote(prev => prev === p.id ? null : p.id)}
                        >
                          {p.isYou && <span className="block font-mono text-[9px] font-normal lowercase tracking-wider mt-1" style={{ color: 'inherit', opacity: 0.7 }}>(you)</span>}
                          {p.eliminated && (
                            <span className="block font-mono text-[9px] font-normal lowercase tracking-wider mt-1">
                              {p.revealedStatus === 'infected' ? 'was infected ☣' : 'was clean ✚'}
                            </span>
                          )}
                          {!p.eliminated && p.shieldRound === round && round > 0 && (
                            <span className="block font-mono text-[9px] font-normal mt-1" style={{ color: '#84cc16' }}>⊕ shielded</span>
                          )}
                          {phase === 'voting' && tallyCount > 0 && !p.eliminated && (
                            <span className="block font-mono text-[9px] font-normal mt-1" style={{ color: '#ff6b6b' }}>{tallyCount} vote{tallyCount === 1 ? '' : 's'}</span>
                          )}
                          {phase === 'gameover' && !p.eliminated && p.revealedStatus === 'infected' && (
                            <span className="block font-mono text-[9px] font-normal mt-1" style={{ color: '#ff6b6b' }}>infected ☣</span>
                          )}
                        </PlayerCard>
                      )
                    })}
                  </div>
                  {canVote && (
                    <p className="mt-3 font-mono text-xs text-center" style={{ color: '#8fa882' }}>Tap a player to select, then cast your vote below.</p>
                  )}
                </article>

                {/* Phase action panel (starting is hoisted above the board) */}
                {phase === 'infection' && (
                  <div className="rounded-lg border p-5" style={{ borderColor: 'rgba(230,51,41,0.4)', backgroundColor: 'rgba(230,51,41,0.08)' }}>
                    <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#e63329' }}>Infection Phase</p>
                    <p className="mt-3 font-mono text-sm leading-relaxed" style={{ color: '#8fa882' }}>
                      {round === 1
                        ? 'The plague is choosing Patient Zero at random. Only the infected player will know…'
                        : 'Patient Zero is silently spreading the plague to a new host…'}
                    </p>
                    {countdown > 0 && (
                      <p className="mt-2 font-mono text-xs" style={{ color: '#7d9a72' }}>Resolving in {countdown}s…</p>
                    )}
                  </div>
                )}

                {phase === 'discussion' && (
                  <DiscussionPanel
                    you={you}
                    round={round}
                    shielded={youShielded}
                    onActivate={activateShield}
                    onSkip={goVoting}
                  />
                )}

                {phase === 'voting' && !you.eliminated && !youVoted && (
                  <div className="rounded-lg border p-5" style={{ borderColor: 'rgba(245,197,24,0.4)', backgroundColor: 'rgba(245,197,24,0.06)' }}>
                    <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#f5c518' }}>Voting Phase</p>
                    <p className="mt-3 font-mono text-sm leading-relaxed" style={{ color: '#8fa882' }}>
                      Vote out the suspected carrier. Players who activated a Shield this round are provably clean — look at everyone else.
                    </p>
                    <p className="mt-2 font-mono text-xs" style={{ color: '#e63329' }}>
                      ⚠ If the timer runs out before you vote, a self-vote is recorded against you.
                    </p>
                    {you.isPatientZero && (
                      <p className="mt-2 font-mono text-xs" style={{ color: '#e63329' }}>
                        ☣ You are Patient Zero — your vote also secretly marks the next infection target.
                      </p>
                    )}
                    <button
                      onClick={castVote}
                      disabled={!selectedVote}
                      className="mt-3 w-full rounded border py-2 font-mono text-sm font-bold uppercase tracking-wider transition-all hover:opacity-90 disabled:opacity-40"
                      style={{ backgroundColor: '#e63329', borderColor: '#e63329', color: '#d4c9b2' }}
                    >
                      {selectedVote
                        ? `Cast Vote Against ${players.find(p => p.id === selectedVote)?.name}`
                        : 'Select a Player Above'}
                    </button>
                  </div>
                )}

                {phase === 'voting' && !you.eliminated && youVoted && (
                  <div className="rounded-lg border p-5" style={{ borderColor: 'rgba(107,142,35,0.35)', backgroundColor: 'rgba(107,142,35,0.08)' }}>
                    <p className="font-mono text-xs" style={{ color: '#6b8e23' }}>
                      ✓ Vote cast against <strong style={{ color: '#d4c9b2' }}>{players.find(p => p.id === votes[YOU_ID])?.name}</strong>. Waiting for the rest of the room…
                    </p>
                  </div>
                )}

                {phase === 'voting' && you.eliminated && (
                  <div className="rounded-lg border p-5" style={{ borderColor: 'rgba(74,94,68,0.5)', backgroundColor: 'rgba(74,94,68,0.1)' }}>
                    <p className="font-mono text-xs" style={{ color: '#7d9a72' }}>⊘ You have been eliminated — spectating while the room votes.</p>
                  </div>
                )}

                {phase === 'reveal' && (
                  <RevealPanel players={players} eliminatedIds={eliminatedIds} noElimination={noElimination} countdown={countdown} />
                )}

                {phase === 'gameover' && (
                  <GameOverPanel
                    outcome={outcome}
                    maxRoundsHit={maxRoundsHit}
                    winners={winners}
                    potPerWinner={potPerWinner}
                    youWon={outcome !== null && winners.includes('You')}
                    onReset={resetDemo}
                  />
                )}

                {/* Desktop: feed balances the left column (like the live game) */}
                <div className="hidden lg:block">
                  {feedCard}
                </div>
              </div>

              {/* Right: chat + feed + role */}
              <aside className="flex flex-col gap-6">

                {/* Room chat — same rules as the live game */}
                <div className="rounded-lg border p-4 flex flex-col" style={{ backgroundColor: '#0a100a', borderColor: 'rgba(107,142,35,0.15)' }}>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] flex-shrink-0" style={{ color: '#6b8e23' }}>Room Chat</p>
                  <div
                    ref={chatScrollRef}
                    onScroll={e => { const el = e.currentTarget; chatPinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40 }}
                    className="mt-3 overflow-y-auto space-y-2 pr-1"
                    style={{ maxHeight: '16rem', scrollbarWidth: 'thin' }}
                  >
                    {chat.length === 0 ? (
                      <p className="font-mono text-[11px]" style={{ color: '#7d9a72' }}>No messages yet…</p>
                    ) : (
                      chat.map((m, i) => (
                        <div key={`${m.senderId}-${i}`} className="font-mono text-[11px] leading-snug break-words">
                          <span style={{ color: m.senderId === YOU_ID ? '#6b8e23' : '#f5c518' }}>{m.name}</span>
                          <span style={{ color: '#7d9a72' }}>: </span>
                          <span style={{ color: '#d4c9b2' }}>{m.text}</span>
                        </div>
                      ))
                    )}
                  </div>
                  {/* Composing indicator — simulated here, a real socket signal
                      in the live game. Reserves its line so the log never
                      reflows as bots start and stop. */}
                  <div
                    className="mt-1 h-4 flex-shrink-0 overflow-hidden font-mono text-[10px] italic"
                    style={{ color: '#7d9a72' }}
                    aria-live="polite"
                  >
                    {typingIds.length > 0 && (
                      <span style={{ animation: 'presence-fade 1.8s ease-in-out infinite' }}>
                        {typingIds.length === 1
                          ? `${players.find(p => p.id === typingIds[0])?.name ?? 'Someone'} is typing…`
                          : `${typingIds.length} players are typing…`}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex gap-2 flex-shrink-0">
                    <input
                      type="text"
                      placeholder={chatBlockedReason ?? 'Say something…'}
                      value={chatInput}
                      maxLength={256}
                      onChange={e => setChatInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') sendChat() }}
                      disabled={chatBlockedReason !== null}
                      className="flex-1 rounded border bg-transparent px-3 py-1.5 font-mono text-xs focus:outline-none"
                      style={{ borderColor: 'rgba(107,142,35,0.3)', color: '#d4c9b2' }}
                    />
                    <button
                      onClick={sendChat}
                      disabled={!chatInput.trim() || chatBlockedReason !== null}
                      className="rounded border px-3 py-1.5 font-mono text-xs uppercase tracking-wider transition-all hover:opacity-90 disabled:opacity-40"
                      style={{ borderColor: '#6b8e23', color: '#6b8e23' }}
                    >
                      Send
                    </button>
                  </div>
                  {chatBlockedReason && (
                    <p className="mt-2 font-mono text-[11px]" style={{ color: '#f5c518' }}>{chatBlockedReason}</p>
                  )}
                </div>

                {/* Your role — stays beside the chat on desktop */}
                {roleCard}

                {/* Mobile: feed stacks under the chat */}
                <div className="lg:hidden">
                  {feedCard}
                </div>

                <Link
                  href="/how-to-play"
                  className="rounded-lg border py-3 text-center font-mono text-xs uppercase tracking-widest transition-all hover:opacity-80"
                  style={{ borderColor: 'rgba(107,142,35,0.3)', color: '#6b8e23', display: 'block' }}
                >
                  How to Play →
                </Link>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}

// ── Sub-panels ────────────────────────────────────────────────────────────────

function StartingPanel({ shieldSet, onCommit }: { shieldSet: boolean; onCommit: (pw: string) => void }) {
  const [input, setInput] = useState('')

  if (shieldSet) {
    return (
      <div className="rounded-lg border p-5" style={{ borderColor: 'rgba(107,142,35,0.35)', backgroundColor: 'rgba(107,142,35,0.08)' }}>
        <p className="font-mono text-xs" style={{ color: '#6b8e23' }}>✓ Shield Password set. Waiting for all players — the game is starting…</p>
      </div>
    )
  }

  // Amber + pulse while pending, matching the live game's "action required"
  // treatment — the demo teaches the cues players will meet for real money.
  return (
    <div className="toxic-pulse rounded-lg">
      <div
        className="rounded-lg border p-5"
        style={{ borderColor: 'rgba(245,197,24,0.7)', backgroundColor: 'rgba(245,197,24,0.08)', boxShadow: '0 0 18px rgba(245,197,24,0.25)' }}
      >
        <p className="font-mono text-xs font-bold uppercase tracking-[0.2em]" style={{ color: '#f5c518' }}>
          ⚠ Action required — Set Shield Password
        </p>
        <p className="mt-2 font-mono text-xs leading-relaxed" style={{ color: '#8fa882' }}>
          Choose a secret password and <strong style={{ color: '#d4c9b2' }}>remember it</strong> — you&apos;ll need
          the exact same one later to activate your Shield. It proves your innocence without
          the contract ever seeing the password itself.
        </p>
        <PasswordInput
          placeholder="My Shield Password…"
          value={input}
          onChange={setInput}
          onKeyDown={e => { if (e.key === 'Enter' && input.trim()) onCommit(input) }}
          borderColor="rgba(245,197,24,0.5)"
          className="mt-3"
        />
        <button
          onClick={() => input.trim() && onCommit(input)}
          disabled={!input.trim()}
          className="mt-3 w-full rounded border py-2 font-mono text-sm font-bold uppercase tracking-wider transition-all hover:opacity-90 disabled:opacity-40"
          style={{ backgroundColor: '#f5c518', borderColor: '#f5c518', color: '#060b06' }}
        >
          Set Shield Password
        </button>
        <p className="mt-2 font-mono text-[10px]" style={{ color: '#7d9a72' }}>
          The game can&apos;t start until every player has set one.
        </p>
      </div>
    </div>
  )
}

function DiscussionPanel({
  you,
  round,
  shielded,
  onActivate,
  onSkip,
}: {
  you: DemoPlayer
  round: number
  shielded: boolean
  onActivate: () => void
  onSkip: () => void
}) {
  if (you.eliminated) {
    return (
      <div className="rounded-lg border p-5" style={{ borderColor: 'rgba(74,94,68,0.5)', backgroundColor: 'rgba(74,94,68,0.1)' }}>
        <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#7d9a72' }}>⊘ Eliminated</p>
        <p className="mt-2 font-mono text-xs leading-relaxed" style={{ color: '#7d9a72' }}>
          You&apos;re out of the game. Watch the survivors argue it out — the round advances automatically.
        </p>
      </div>
    )
  }

  if (you.status === 'infected') {
    return (
      <div className="rounded-lg border p-5" style={{ borderColor: 'rgba(230,51,41,0.5)', backgroundColor: 'rgba(230,51,41,0.08)' }}>
        <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#e63329' }}>
          {you.isPatientZero ? '☣ You are Patient Zero' : '⚠ You are Infected'}
        </p>
        <p className="mt-2 font-mono text-xs leading-relaxed" style={{ color: '#ff6b6b' }}>
          Infected players cannot activate a Shield — the ZK circuit rejects the proof. Use the chat to deflect suspicion, then vote a clean player out.
        </p>
        <button
          onClick={onSkip}
          className="mt-3 w-full rounded border py-2 font-mono text-sm uppercase tracking-wider transition-all hover:opacity-80"
          style={{ borderColor: 'rgba(245,197,24,0.5)', color: '#f5c518', backgroundColor: 'transparent' }}
        >
          Skip to Voting →
        </button>
      </div>
    )
  }

  return (
    <div className="rounded-lg border p-5" style={{ borderColor: 'rgba(107,142,35,0.35)', backgroundColor: 'rgba(107,142,35,0.08)' }}>
      <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#6b8e23' }}>Discussion Phase — Round {round}</p>
      <p className="mt-2 font-mono text-xs leading-relaxed" style={{ color: '#8fa882' }}>
        Someone new is infected. Talk it out in chat — and if suspicion lands on you, activate your Shield to prove innocence. Infected players <em>cannot</em> activate one.
      </p>
      {shielded ? (
        <div className="mt-3 space-y-3">
          <p className="font-mono text-xs" style={{ color: '#84cc16' }}>✓ Shield active — you are provably clean this round. A top vote cannot eliminate you.</p>
          <button
            onClick={onSkip}
            className="w-full rounded border py-2 font-mono text-sm font-bold uppercase tracking-wider transition-all hover:opacity-90"
            style={{ borderColor: '#f5c518', color: '#f5c518', backgroundColor: 'rgba(245,197,24,0.08)' }}
          >
            Skip to Voting →
          </button>
        </div>
      ) : (
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onActivate}
            className="flex-1 rounded border py-2 font-mono text-sm font-bold uppercase tracking-wider transition-all hover:opacity-90"
            style={{ backgroundColor: '#6b8e23', borderColor: '#6b8e23', color: '#060b06' }}
          >
            Activate Shield
          </button>
          <button
            onClick={onSkip}
            className="flex-1 rounded border py-2 font-mono text-sm uppercase tracking-wider transition-all hover:opacity-80"
            style={{ borderColor: 'rgba(245,197,24,0.5)', color: '#f5c518', backgroundColor: 'transparent' }}
          >
            Skip to Voting →
          </button>
        </div>
      )}
    </div>
  )
}

function RevealPanel({
  players,
  eliminatedIds,
  noElimination,
  countdown,
}: {
  players: DemoPlayer[]
  eliminatedIds: string[]
  noElimination: boolean
  countdown: number
}) {
  const eliminated = players.filter(p => eliminatedIds.includes(p.id))
  const anyInfected = eliminated.some(p => p.revealedStatus === 'infected')

  let borderColor = 'rgba(230,51,41,0.4)'
  let accent = '#e63329'
  if (noElimination) { borderColor = 'rgba(143,168,130,0.4)'; accent = '#8fa882' }
  else if (anyInfected) { borderColor = 'rgba(132,204,22,0.4)'; accent = '#84cc16' }

  return (
    <div className="rounded-lg border p-5" style={{ borderColor, backgroundColor: `${accent}10` }}>
      <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: accent }}>
        {noElimination ? 'No Elimination' : anyInfected ? 'Carrier Down' : 'Wrong Elimination'}
      </p>
      {noElimination ? (
        <p className="mt-3 font-mono text-sm leading-relaxed" style={{ color: '#8fa882' }}>
          Every top-voted player had an active Shield. Nobody was eliminated — the game moves on.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {eliminated.map(p => (
            <p key={p.id} className="font-mono text-sm" style={{ color: p.revealedStatus === 'infected' ? '#84cc16' : '#e63329' }}>
              {p.name} eliminated — was {p.revealedStatus === 'infected' ? 'INFECTED ☣' : 'CLEAN ✚'}
              {p.revealedStatus === 'clean' && '. The plague is still among you.'}
            </p>
          ))}
        </div>
      )}
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: '#7d9a72' }}>
        {countdown > 0 ? `Next round in ${countdown}s…` : 'Resolving…'}
      </p>
    </div>
  )
}

function YourRoleCard({ you }: { you: DemoPlayer }) {
  let label = 'Clean'
  let color = '#6b8e23'
  let desc = 'Survive, find the carriers, and vote them out before the infected reach parity.'
  if (you.eliminated) {
    label = 'Eliminated'
    color = '#7d9a72'
    desc = 'You are out. Spectate the rest of the outbreak.'
  } else if (you.isPatientZero) {
    label = 'Patient Zero ☣'
    color = '#e63329'
    desc = 'You lead the infection. Your vote secretly marks the next infection target. Avoid suspicion.'
  } else if (you.status === 'infected') {
    label = 'Infected ⚠'
    color = '#e63329'
    desc = 'You carry the plague. You cannot Shield — bluff your way to parity.'
  }
  return (
    <div className="rounded-lg border p-5" style={{ backgroundColor: 'rgba(107,142,35,0.04)', borderColor: 'rgba(107,142,35,0.2)' }}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: '#7d9a72' }}>Your Role</p>
      <p className="font-mono text-sm" style={{ color }}>{label}</p>
      <p className="mt-2 font-mono text-xs leading-relaxed" style={{ color: '#8fa882' }}>{desc}</p>
    </div>
  )
}

function GameOverPanel({
  outcome,
  maxRoundsHit,
  winners,
  potPerWinner,
  youWon,
  onReset,
}: {
  outcome: GameOutcome | null
  maxRoundsHit: boolean
  winners: string[]
  potPerWinner: number
  youWon: boolean
  onReset: () => void
}) {
  const won = outcome === 'clean_win'
  let title = 'DRAW'
  let color = '#f5c518'
  if (outcome === 'clean_win') { title = 'CLEAN WIN'; color = '#84cc16' }
  if (outcome === 'infected_win') { title = 'INFECTED WIN'; color = '#e63329' }

  return (
    <div className="space-y-6">
      <div
        className="rounded-lg border p-6"
        style={{ borderColor: `${color}66`, backgroundColor: `${color}0f` }}
      >
        <p className="font-display text-4xl leading-none" style={{ color }}>{title}</p>
        {maxRoundsHit && (
          <p className="mt-2 font-mono text-xs" style={{ color: '#8fa882' }}>Round limit reached — the game ends as a draw.</p>
        )}
        <p className="mt-3 font-mono text-sm leading-relaxed" style={{ color: '#8fa882' }}>
          {outcome === 'draw'
            ? 'A standoff — neither faction closed it out. Every player still alive splits the pot, infected or not.'
            : won
              ? 'The village purged every carrier. The pot goes to the surviving clean players.'
              : 'The infected reached parity with the living. The pot goes to the surviving infected.'}
        </p>
        {winners.length > 0 && (
          <p className="mt-3 font-mono text-sm" style={{ color: '#f5c518' }}>
            {youWon ? '★ You survived and won' : 'Winners'}: {winners.join(', ')} — {potPerWinner.toFixed(2)} USDm each
            <span style={{ color: '#7d9a72' }}> (pot {POT_TOTAL} USDm − 1.5% platform fee)</span>
          </p>
        )}
        <p className="mt-3 font-mono text-xs" style={{ color: '#7d9a72' }}>
          In real games all of this runs on Celo: stakes locked in the contract, Shields verified as ZK proofs on-chain, payouts automatic.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Link
          href="/lobby"
          className="flex-1 rounded-lg py-4 text-center font-mono text-sm font-bold uppercase tracking-widest transition-all hover:opacity-90 active:scale-95"
          style={{ backgroundColor: '#cc1414', color: '#ffffff', boxShadow: '4px 4px 0 #6b8e23' }}
        >
          Play for Real →
        </Link>
        <button
          onClick={onReset}
          className="flex-1 rounded-lg border py-4 text-center font-mono text-sm uppercase tracking-wider transition-all hover:opacity-80"
          style={{ borderColor: 'rgba(245,197,24,0.5)', color: '#f5c518' }}
        >
          Run Demo Again
        </button>
        <Link
          href="/how-to-play"
          className="flex-1 rounded-lg border py-4 text-center font-mono text-sm uppercase tracking-wider transition-all hover:opacity-80"
          style={{ borderColor: 'rgba(107,142,35,0.4)', color: '#6b8e23' }}
        >
          How It Works →
        </Link>
      </div>
    </div>
  )
}
